/*
  Behavior for sections/nav-menu.liquid.
  Independent from tramas-nav.js — do not share state with it.

  Handles:
  - [data-nav-toggle] buttons: open/close a submenu for touch/keyboard users
    (desktop pointer users also get the submenu via CSS :hover; below the
    mobile breakpoint the same toggle drives an accordion instead).
  - [data-nav-hover-label] elements: items configured as "solo hover" never
    navigate; tapping/activating them just toggles their submenu, same as
    the toggle button.
  - [data-nav-mobile-toggle]: the hamburger button that opens/closes the
    whole level-1 list as a full-screen panel below the mobile breakpoint
    (see nav-menu.css). Also toggles body.nav-menu-scroll-lock, so the
    page behind it can't be scrolled while it's open — same convention
    the theme's own drawers use for their own body-lock class.
  - .nav-menu-spacer: `.nav-menu` is `position: fixed` (see nav-menu.css for
    why), so this measures the nav's real rendered height and feeds it to
    the spacer that reserves that space in the flow. Skipped while the
    mobile panel is open, since then the nav's height includes the panel.
  - .nav-menu__announcement: rotates its message items automatically.
*/
(function () {
  function syncSpacerHeights() {
    document.querySelectorAll('.nav-menu').forEach(function (nav) {
      if (nav.getAttribute('data-mobile-open') === 'true') return;
      var spacer = nav.nextElementSibling;
      if (!spacer || !spacer.classList.contains('nav-menu-spacer')) return;
      var height = nav.getBoundingClientRect().height + 'px';
      // Set on :root, not just on the spacer: .nav-menu__mega-panel (used
      // for the desktop mega menu) needs this value too, and it isn't a
      // descendant of the spacer, so it wouldn't inherit a value set there.
      document.documentElement.style.setProperty('--nav-menu-height', height);
    });
  }

  if ('ResizeObserver' in window) {
    var observer = new ResizeObserver(syncSpacerHeights);
    document.querySelectorAll('.nav-menu').forEach(function (nav) {
      observer.observe(nav);
    });
  } else {
    window.addEventListener('resize', syncSpacerHeights);
    window.addEventListener('load', syncSpacerHeights);
  }
  syncSpacerHeights();

  // Sets/clears data-nav-expanded on one item plus its own toggle/label
  // aria-expanded state — the single place that touches those three attrs.
  function setExpanded(item, expanded) {
    if (expanded) {
      item.setAttribute('data-nav-expanded', 'true');
    } else {
      item.removeAttribute('data-nav-expanded');
    }
    var state = String(expanded);
    var toggle = item.querySelector(':scope > [data-nav-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', state);
    var label = item.querySelector(':scope > [data-nav-hover-label]');
    if (label) label.setAttribute('aria-expanded', state);
  }

  // Closes an item and any of its own open descendants (so re-opening it
  // later starts collapsed), without touching its ancestors or siblings.
  function closeItemAndDescendants(item) {
    setExpanded(item, false);
    item.querySelectorAll('.nav-menu__item[data-nav-expanded="true"]').forEach(function (descendant) {
      setExpanded(descendant, false);
    });
  }

  // Closes every open item on the page — for "click outside" / Escape,
  // where the whole nav should reset, not just one branch of it.
  function closeAll() {
    document.querySelectorAll('.nav-menu__item[data-nav-expanded="true"]').forEach(function (item) {
      setExpanded(item, false);
    });
  }

  function closeMobilePanel(nav) {
    if (!nav || nav.getAttribute('data-mobile-open') !== 'true') return;
    nav.setAttribute('data-mobile-open', 'false');
    document.body.classList.remove('nav-menu-scroll-lock');
    var hamburger = nav.querySelector(':scope > [data-nav-mobile-toggle]');
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
    closeAll();
    syncSpacerHeights();
  }

  // Opening an item only closes its *siblings* (same level, same parent) —
  // never its ancestors — so expanding a level-2 item keeps its level-1
  // parent open instead of collapsing it.
  function toggleItem(item) {
    var isOpen = item.getAttribute('data-nav-expanded') === 'true';

    if (isOpen) {
      closeItemAndDescendants(item);
      return;
    }

    var siblingList = item.parentElement;
    if (siblingList) {
      Array.prototype.forEach.call(siblingList.children, function (sibling) {
        if (sibling !== item && sibling.getAttribute && sibling.getAttribute('data-nav-expanded') === 'true') {
          closeItemAndDescendants(sibling);
        }
      });
    }

    setExpanded(item, true);
  }

  // Set while a [data-nav-mobile-toggle] click is handled, and cleared on
  // the next tick. Guards against this scenario: some *other* button that
  // lives outside .nav-menu (e.g. a floating dock's own catalog button)
  // opens this menu from its own click handler by calling
  // mobileToggle.click(). That call fires and fully finishes this same
  // listener for its own (inner) click event — but the *original* click on
  // that external button is still bubbling, and once it reaches document
  // its target is outside .nav-menu, so without this guard the "click
  // outside closes the menu" branch below would immediately undo what was
  // just opened, in the same synchronous turn.
  var suppressOutsideClose = false;

  document.addEventListener('click', function (event) {
    var mobileToggle = event.target.closest('[data-nav-mobile-toggle]');
    if (mobileToggle) {
      var nav = mobileToggle.closest('.nav-menu');
      if (!nav) return;
      var isOpen = nav.getAttribute('data-mobile-open') === 'true';
      if (isOpen) {
        closeMobilePanel(nav);
      } else {
        nav.setAttribute('data-mobile-open', 'true');
        document.body.classList.add('nav-menu-scroll-lock');
        mobileToggle.setAttribute('aria-expanded', 'true');
      }
      suppressOutsideClose = true;
      setTimeout(function () { suppressOutsideClose = false; }, 0);
      return;
    }

    var toggle = event.target.closest('[data-nav-toggle]');
    var hoverLabel = event.target.closest('[data-nav-hover-label]');
    var control = toggle || hoverLabel;

    if (control) {
      event.preventDefault();
      var item = control.closest('.nav-menu__item');
      if (item) toggleItem(item);
      return;
    }

    // Mobile only: expand a parent item on a tap anywhere in its row, not
    // just its toggle icon (or, for "solo hover" items, its title) — the
    // gap between a title and the toggle (margin-left:auto pushes it to
    // the row's right edge) was previously dead space. closest() here
    // always resolves to the item nearest the tap, so a tap inside an
    // open level-1 panel toggles the level-2/3 item actually tapped, never
    // an ancestor. The title itself is excluded so a "Clicable (navega)"
    // item can still be tapped there to navigate normally — this only
    // extends what happens on the *rest* of the row. Desktop is untouched:
    // its mega panel already opens on :hover, and a stray click on that
    // same empty space would otherwise leave data-nav-expanded stuck on
    // after the pointer moves away.
    var rowItem = event.target.closest('.nav-menu__item');
    if (
      rowItem &&
      rowItem.classList.contains('has-children') &&
      !event.target.closest('.nav-menu-item__label') &&
      window.matchMedia('(max-width: 1023.98px)').matches
    ) {
      event.preventDefault();
      toggleItem(rowItem);
      return;
    }

    // A normal link inside the mobile panel: let it navigate, just close
    // the accordion state (irrelevant once the page unloads/navigates).
    if (event.target.closest('.nav-menu')) return;

    if (suppressOutsideClose) return;

    closeAll();
    closeMobilePanel(document.querySelector('.nav-menu[data-mobile-open="true"]'));
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var hoverLabel = event.target.closest('[data-nav-hover-label]');
    if (!hoverLabel) return;
    event.preventDefault();
    var item = hoverLabel.closest('.nav-menu__item');
    if (item) toggleItem(item);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    closeAll();
    closeMobilePanel(document.querySelector('.nav-menu[data-mobile-open="true"]'));
  });

  // Announcement bar: rotates through .nav-menu__announcement-item children,
  // one visible ("is-active") at a time, every data-interval seconds.
  document.querySelectorAll('.nav-menu__announcement').forEach(function (bar) {
    var items = bar.querySelectorAll('.nav-menu__announcement-item');
    if (items.length < 2) return;
    var interval = (parseFloat(bar.getAttribute('data-interval')) || 3) * 1000;
    var current = 0;
    setInterval(function () {
      items[current].classList.remove('is-active');
      current = (current + 1) % items.length;
      items[current].classList.add('is-active');
    }, interval);
  });
})();
